package com.eladfinish.bible.contest.ui.menus.viewModels

import androidx.lifecycle.ViewModel
import com.eladfinish.bible.contest.common.classNameOf
import com.eladfinish.bible.contest.ui.menus.actions.ToolbarAction
import com.eladfinish.bible.contest.ui.menus.actions.ToolbarActionEmitter
import io.github.aakira.napier.Napier
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.asSharedFlow

/**
 * A centralized event bus for dispatching toolbar actions to the currently
 * active screen.
 *
 * This ViewModel acts as a bridge between a Compose-based toolbar (which emits
 * user interactions) and Fragment-owned business logic (which consumes them).
 *
 * Design goals:
 * - Decouple toolbar UI from screen implementations
 * - Support multiple screens with independent toolbar actions
 * - Avoid direct references between Activity, Compose, and Fragments
 * - Remain lifecycle-safe and scalable as the number of screens grows
 *
 * How it works:
 * - The hosting Activity updates the active screen via [setActiveScreenKey]
 *   based on navigation changes.
 * - Toolbar UI emits a [ToolbarAction] using [emit].
 * - The bus attaches the currently active `screenKey` to the action and
 *   publishes it as an [Event].
 * - Screens (Fragments) collect [events] and react only to actions that
 *   match their own `screenKey`.
 *
 * Event delivery characteristics:
 * - Backed by [MutableSharedFlow] with buffering for fast, fire-and-forget
 *   toolbar interactions (e.g., button clicks).
 * - If no active screen is set, emitted actions are safely ignored.
 *
 * Typical flow:
 * 1. Navigation changes → Activity calls [setActiveScreenKey]
 * 2. User clicks toolbar action → Compose calls [emit]
 * 3. Fragment collects matching [Event] and executes screen-specific logic
 */
class ToolbarActionBusViewModel : ViewModel() {

    /**
     * Represents a toolbar action scoped to a specific screen.
     *
     * @property screenKey Unique identifier of the target screen.
     * @property action Screen-specific toolbar action.
     */
    data class Event(
        val screenKey: String,
        val action: ToolbarAction,
    )

    /**
     * Holds the screen key of the currently active navigation destination.
     * Updated by the hosting Activity in response to navigation changes.
     */
    private val _activeScreenKey = MutableStateFlow<String?>(null)

    /**
     * Internal shared flow used to broadcast toolbar events.
     */
    private val _events = MutableSharedFlow<Event>(extraBufferCapacity = 64)

    /**
     * Public stream of toolbar events consumed by screens.
     */
    val events: SharedFlow<Event> = _events.asSharedFlow()

    /**
     * Updates the currently active screen.
     *
     * This method should be called by the Activity whenever the navigation
     * destination changes.
     *
     * @param screenKey The unique key of the active screen, or null if no
     *                  screen is currently active.
     */
    fun setActiveScreenKey(screenKey: String?) {
        _activeScreenKey.value = screenKey
    }

    /**
     * Emits a toolbar action for the currently active screen.
     *
     * If no active screen is set, the action is ignored.
     *
     * Note: this method is intentionally generic to keep call sites strongly-typed:
     * callers can pass any subtype of [ToolbarAction] without casting.
     *
     * @param action The toolbar action triggered by the user.
     */
    fun <TAction : ToolbarAction> emit(action: TAction) {
        val screenKey = _activeScreenKey.value ?: return
        val value = Event(screenKey = screenKey, action = action)
        Napier.d("Emitting toolbar action: $value - screenKey [$screenKey]")
        _events.tryEmit(value)
    }
}

inline fun <reified TAction : ToolbarAction> ToolbarActionBusViewModel.emitter(): ToolbarActionEmitter<TAction> {
    return ToolbarActionEmitter { action ->
        Napier.d("ToolbarActionEmitter action: ${classNameOf(action::class)}")
        emit(action)
    }
}
